import React, { useCallback, useRef, useState } from 'react';
import { MdChecklistRtl } from 'react-icons/md';
import { saveAs } from 'file-saver';

import { DEFAULT_PAGE_LIMIT } from 'legacy/events/service';
import {
  useGetRequestApprovals,
  useExportRequestApprovalList
} from 'legacy/events/hooks';

import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Loader from 'legacy/common/components/layout/Loader';

import RequestListRow from '../RequestListRow';
import RequestListDetailModal from '../RequestDetailModal';

interface RequestListModalProps {
  total: number;
  eventId: string;
  onClose: () => void;
}

const RequestListModal: React.FC<RequestListModalProps> = ({
  total,
  eventId,
  onClose
}) => {
  const [pageNumber, setPageNumber] = useState(0);

  // For the request detail modal
  const [detailViewIndex, setDetailViewIndex] = useState<number | null>(null);
  const { data, isLoading, isFetching, fetchNextPage } = useGetRequestApprovals(
    {
      eventId
    }
  );

  const ref = useRef<IntersectionObserver>(null);
  const lastElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isFetching) {
        return;
      }

      if (ref.current) {
        ref.current.disconnect();
      }

      ref.current = new IntersectionObserver((entries) => {
        if (
          entries[0].isIntersecting &&
          data?.pages?.[data.pages.length - 1]?.items.length ===
            DEFAULT_PAGE_LIMIT
        ) {
          fetchNextPage({
            pageParam: pageNumber + 1
          });
          setPageNumber((pageNumber) => pageNumber + 1);
        }
      });

      if (node) {
        ref.current.observe(node);
      }
    },
    [isFetching, pageNumber]
  );

  const { refetch: fetchExcelData, isFetching: isExporting } =
    useExportRequestApprovalList(eventId, {
      isEnabled: false
    });

  const onClickDownloadCSV = async () => {
    const { data: rawData } = await fetchExcelData();
    const blob = new Blob([rawData], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8'
    });

    saveAs(blob, 'Bounce-Guests.xlsx');
  };

  const onRequestClick = (index: number) => {
    setDetailViewIndex(index);
  };

  const onDetailClose = () => {
    setDetailViewIndex(null);
  };

  const flattenedData = data?.pages?.flatMap((page) => page.items) || [];
  const onSkip = () => {
    setDetailViewIndex((index) => {
      if (flattenedData?.length === index + 1) {
        return null;
      }

      return index + 1;
    });
  };

  return (
    <>
      <Modal
        type={ModalType.SMALL}
        onClose={onClose}
        visible
        icon={<MdChecklistRtl size={20} />}
        title={`Request list (${total})`}
        renderFooter={() => (
          <Button
            type={ButtonType.Inverted}
            onClick={onClickDownloadCSV}
            icon={<img src="/img/events/csv.svg" />}
            loading={isExporting}
          >
            Download CSV
          </Button>
        )}
        isBehind={detailViewIndex !== null}
      >
        <div className="px-4 py-6">
          <Loader isLoading={isLoading} />
          {!isLoading && flattenedData?.length === 0 && (
            <div className="w-full h-full flex flex-col items-center justify-center py-20">
              <img src="/img/events/empty-request-list.png" className="w-28" />
              <p className="text-sm text-gray-500 text-center">
                Ticket requests will appear here
              </p>
              <p className="text-sm text-gray-500 px-10 text-center mt-4">
                You can view all your approved/rejected requests by downloading
                the CSV
              </p>
            </div>
          )}

          {flattenedData?.map((request, index) => (
            <RequestListRow
              eventId={eventId}
              request={request}
              key={request.id}
              ref={lastElementRef}
              onClick={() => onRequestClick(index)}
            />
          ))}
        </div>
      </Modal>

      {detailViewIndex !== null && (
        <RequestListDetailModal
          eventId={eventId}
          request={flattenedData?.[detailViewIndex]}
          onClose={onDetailClose}
          onSkip={onSkip}
        />
      )}
    </>
  );
};

export default RequestListModal;
